<template>
  <div
    class="multiselect"
    :class="{ active: dropdownOpen }"
    @blur="dropdownOpen = false"
    @click="toggleDropdown()"
  >
    <div class="selected">
      <span
        v-if="itemsSelectedLabel"
        class="body-3"
      >
        {{ itemsSelectedLabel }}
      </span>
      <span
        v-else
        class="placeholder body-4"
      >
        {{ blok.placeholder }}
      </span>
    </div>
    <div
      class="options"
      :class="{ active: dropdownOpen }"
    >
      <div
        v-for="item in items"
        :key="item.code"
        class="options__item body-1"
        @click="selectItem(item.code)"
      >
        <input
          type="checkbox"
          :aria-label="item.title"
          :checked="isSelected(item.code)"
        />
        <span>{{ item.title }} ({{ item.count }})</span>
        <img
          v-if="isSelected(item.code)"
          src="~/assets/icons/close.svg"
          alt="Close"
        />
      </div>

      <GeneralElementsButton
        v-if="filterStore.teams.length"
        :label="$t('vacancies.filter.clear')"
        class="clear"
        @clicked="clearSelected"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { useFilterStore, useTeamsStore } from '~/stores'
  import type ITeam from '~/types/Storyblok/Vacancy/Team'

  defineProps<{
    blok: {
      placeholder: string
    }
  }>()

  const { locale } = useI18n()
  const filterStore = useFilterStore()
  const teamsStore = useTeamsStore()

  const items = computed(() => teamsStore.teams)

  const dropdownOpen = ref<boolean>(false)
  const itemsSelectedLabel = ref<string>('')

  function toggleDropdown() {
    dropdownOpen.value = !dropdownOpen.value
  }

  function selectItem(item: string) {
    const index: number = filterStore.teams.indexOf(item)

    if (index >= 0) {
      filterStore.teams.splice(index, 1)
    } else {
      filterStore.teams.push(item)
    }

    itemsSelectedLabel.value = itemsToString()
    dropdownOpen.value = false
  }

  function isSelected(code: string): boolean {
    return filterStore.teams.findIndex((element) => element === code) >= 0
  }

  function itemsToString(): string {
    const result: string[] = []

    filterStore.teams.forEach((item: string) => {
      const teamTitle = teamsStore.teams.find((team: ITeam) => team.code === item)?.title

      if (teamTitle) {
        result.push(teamTitle)
      }
    })

    return result.join(', ')
  }

  function clearSelected() {
    filterStore.teams = []
    itemsSelectedLabel.value = ''
  }

  onMounted(() => {
    if (!teamsStore.teams.length) {
      teamsStore.fetchTeams(locale.value).then(() => {
        teamsStore.fetchVacancyCount(locale.value)
      })
    }
  })
</script>

<style lang="scss" scoped>
  @import '~/assets/styles/common.scss';

  .multiselect {
    border: 1px solid $form-field-border;
    border-radius: 4px;
    font-size: rem(16);
    max-width: 328px;
    position: relative;
    width: calc(100% - 24px);
    text-overflow: ellipsis;

    @include tablet-up {
      border: none;
      height: auto;
      max-width: 100%;
      padding: 0;
      width: 100%;
    }

    &:after {
      content: url('~/assets/icons/arrow-down.svg');
      margin-left: rem(10);
      position: absolute;
      right: 12px;
      top: 12px;

      @include tablet-up {
        content: '';
      }
    }

    &.active {
      &:after {
        transform: rotate(180deg);
      }
    }

    .selected {
      color: $bt-blue;
      height: 18px;
      line-height: 18px;
      overflow: hidden;
      padding: rem(12);
      width: calc(100% - 20px);

      @include tablet-up {
        display: none;
      }

      span {
        display: inline-block;
        max-width: calc(100% - 20px);
        overflow: hidden;
        text-overflow: ellipsis;
        text-wrap: nowrap;
      }

      .placeholder {
        color: $secondary;
      }
    }
  }

  .options {
    background-color: $white;
    border-radius: 4px;
    box-shadow: 0px 4px 4px rgba(106, 119, 142, 0.15);
    display: none;
    left: 0px;
    padding: rem(8) 0;
    position: absolute;
    top: 44px;
    width: 100%;

    @include tablet-up {
      box-shadow: none;
      display: block;
      left: 0;
      position: relative;
      top: 0;
    }

    &.active {
      display: block;
    }

    &__item {
      color: $bt-blue;
      cursor: pointer;
      margin-bottom: rem(8);
      padding: rem(12);
      position: relative;

      @include tablet-up {
        padding: rem(12) rem(34) rem(12) 0;
      }

      &:hover {
        background-color: $bg-grey;
      }

      input {
        margin-right: rem(16);
        // TODO: Style checkmark
      }

      img {
        position: absolute;
        right: 14px;
        top: 14px;
      }
    }

    .clear {
      @include tablet-up {
        margin-top: rem(30);
      }
    }
  }
</style>
